import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { downloadBlob } from "@/components/DataTable";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft, ChevronRight, FileDown, ExternalLink, Truck } from "lucide-react";
import { toast } from "sonner";
import AuditTrail from "@/components/AuditTrail";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";
import { useAuth } from "@/contexts/AuthContext";

const STATUSES = ["Draft", "Sent", "Acknowledged", "PartiallyReceived", "Received", "Cancelled", "Closed"];
const CAN_EDIT = new Set(["purchase", "sales", "plant_head", "admin", "super_admin"]);

export default function SupplierPoDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get(`/supplier-pos/${id}`);
      setD(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
      nav("/supplier-pos");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  async function exportDoc() {
    const token = localStorage.getItem("hispl_token");
    const res = await fetch(`${API}/supplier-pos/${id}/export`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Export failed"); return; }
    await downloadBlob(res, `SupplierPO_${(d.sp_number || "SP").replace(/[^A-Za-z0-9._-]+/g, "_")}.xlsx`);
    toast.success("Supplier PO exported");
  }

  async function updateStatus(newStatus) {
    setSaving(true);
    try {
      await api.patch(`/supplier-pos/${id}/status`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  }

  if (loading || !d) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;
  const supplier = d.supplier_snapshot || {};
  const canEdit = CAN_EDIT.has(user.role);

  return (
    <div data-testid="supplier-po-detail-page" className="space-y-5 pb-6">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/supplier-pos" className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Supplier POs
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="font-mono text-slate-900">{d.sp_number}</span>
      </div>

      <Card className="p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">
              Supplier Purchase Order · Sub-Contract
            </div>
            <div className="mt-1 font-mono text-xl md:text-2xl font-bold text-slate-900">{d.sp_number}</div>
            <div className="mt-2 flex items-center gap-3 text-sm flex-wrap">
              <span className="text-slate-600 font-medium">{d.supplier_name}</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-600">Scope: <strong className="text-slate-900">{d.scope}</strong></span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500 font-mono text-xs">Delivery: {d.delivery_date || "—"}</span>
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs font-mono text-slate-600 flex-wrap">
              {d.oa_number && (
                <Link to={`/order-acceptances/${d.order_acceptance_id}`} className="text-blue-900 hover:underline flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> From OA {d.oa_number}
                </Link>
              )}
              {d.customer_name && <span>End customer: <strong className="text-slate-900">{d.customer_name}</strong></span>}
              {d.customer_po_number && <span>Cust PO: <strong className="text-slate-900">{d.customer_po_number}</strong></span>}
            </div>
          </div>
          <div className="flex flex-col md:items-end gap-2">
            <div className="flex items-center gap-3">
              <CommercialStatusBadge status={d.status} className="text-xs px-3 py-1" />
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total</div>
                <div className="font-mono text-lg font-bold text-slate-900">₹{(d.total_value || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={exportDoc} data-testid="sp-export-btn">
                <FileDown className="w-3.5 h-3.5 mr-1" /> Export Excel
              </Button>
              {canEdit && (
                <Select value={d.status} onValueChange={updateStatus} disabled={saving}>
                  <SelectTrigger className="w-44 h-9" data-testid="sp-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <AdminDeleteButton endpoint={`/supplier-pos/${id}`} entityName="Supplier PO"
                               entityNumber={d.sp_number} redirectAfter="/supplier-pos" />
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Truck className="w-4 h-4 text-slate-500" /> Supplier</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          {[
            ["Name", supplier.name], ["Code", supplier.code],
            ["GSTIN", supplier.gstin], ["PAN", supplier.pan],
            ["Contact", supplier.contact_person], ["Phone", supplier.phone],
            ["Email", supplier.email],
            ["Address", [supplier.address, supplier.city, supplier.state, supplier.pincode].filter(Boolean).join(", ")],
          ].map(([k, v]) => (
            <div key={k} className="flex gap-3 py-1 border-b border-slate-100 last:border-0">
              <div className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold w-24 flex-shrink-0">{k}</div>
              <div className="text-slate-900 font-mono">{v || "—"}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">Line Items · {d.line_items?.length || 0}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-left">Drawing #</th>
                <th className="px-3 py-2 text-left">Spec</th>
                <th className="px-3 py-2 text-left">UOM</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Rate</th>
                <th className="px-3 py-2 text-right">Value</th>
                <th className="px-3 py-2 text-left">Del.date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(d.line_items || []).map((li) => {
                const specBits = [
                  li.bore_mm && `Ø${li.bore_mm}`,
                  li.rod_diameter_mm && `R${li.rod_diameter_mm}`,
                  li.stroke_mm && `S${li.stroke_mm}`,
                  li.operating_pressure_bar && `${li.operating_pressure_bar} bar`,
                ].filter(Boolean).join(" × ");
                return (
                  <tr key={li.id} data-testid={`sp-line-${li.line_no}`}>
                    <td className="px-3 py-2 font-mono">{li.line_no}</td>
                    <td className="px-3 py-2">{li.description}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{li.customer_drawing_number || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">{specBits || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{li.uom || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{li.quantity}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{li.unit_price != null ? `₹${li.unit_price.toLocaleString("en-IN")}` : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-xs">{li.line_total != null ? `₹${li.line_total.toLocaleString("en-IN")}` : "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{li.delivery_date || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-50 font-semibold">
              <tr>
                <td colSpan={5} className="px-3 py-2 text-right text-xs uppercase tracking-wider text-slate-700">Total</td>
                <td className="px-3 py-2 text-right font-mono">{d.total_qty}</td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-right font-mono text-red-700">₹{(d.total_value || 0).toLocaleString("en-IN")}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {d.remarks && (
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Remarks</div>
          <p className="text-sm text-slate-700 whitespace-pre-line">{d.remarks}</p>
        </Card>
      )}

      <Card className="p-5">
        <div className="text-sm font-semibold text-slate-900 mb-4">Audit Trail</div>
        <AuditTrail items={d.audit_trail || []} />
      </Card>
    </div>
  );
}
